/**
 * Messaging API Service
 * Handles workspace messaging: channels, messages, threads, reactions
 */

import { apiClient, handleApiError } from './apiClient';

// ============================================================================
// TYPES
// ============================================================================

export type ChannelType = 'public' | 'private' | 'direct' | 'announcement';
export type NotificationLevel = 'all' | 'mentions' | 'none';
export type MemberRole = 'owner' | 'admin' | 'member' | 'guest';

export interface SenderInfo {
  user_id: number | null;
  member_id: number | null;
  name: string;
  avatar: string | null;
}

export interface ChannelMember {
  id: number;
  user_id: number | null;
  member_id: number | null;
  name: string;
  avatar: string | null;
  role: MemberRole;
  can_post: boolean;
  notification_level: NotificationLevel;
  unread_count: number;
  joined_at: string;
  is_online: boolean;
}

export interface Channel {
  id: number;
  uuid: string;
  name: string;
  display_name: string | null;
  description: string | null;
  topic: string | null;
  channel_type: ChannelType;
  is_dm: boolean;
  is_archived: boolean;
  is_readonly: boolean;
  member_count: number;
  message_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  icon: string | null;
  color: string | null;
  unread_count: number;
  mention_count: number;
  created_at: string;
  created_by_name: string | null;
  dm_member_id?: number | null; // set for member DM channels (vs staff DMs)
  /** Unread messages marked urgent, when the list endpoint reports it. */
  urgent_unread_count?: number;
  /** TRUS-638: pinned by the caller (per user, per org). List endpoint only. */
  is_pinned?: boolean;
  /** Position among the caller's pins (0 = top); null when not pinned. */
  pin_position?: number | null;
  /** TRUS-684: an active system-event subscription posts into this channel. */
  has_event_subscription?: boolean;
}

export interface ChannelDetail extends Channel {
  members: ChannelMember[];
  pinned_messages: Array<{
    id: number;
    content: string;
    sender_name: string;
    created_at: string;
  }>;
  settings: {
    allow_threads: boolean;
    allow_reactions: boolean;
    allow_file_uploads: boolean;
  };
}

export interface Attachment {
  id: number;
  uuid: string;
  filename: string;
  file_type: string;
  mime_type: string;
  file_size: number;
  url: string | null;
  thumbnail_url: string | null;
  /** 'upload' | 'document' | 'cloud_link' — absent on older rows (= upload). */
  source?: AttachmentSource | null;
  /** Set when source is 'cloud_link'; url is then the provider's web URL (null if it failed host validation). */
  provider?: CloudFileProvider | null;
  provider_file_id?: string | null;
  modified_at?: string | null;
  icon_hint?: string | null;
}

export type AttachmentSource = 'upload' | 'document' | 'cloud_link';
export type CloudFileProvider = 'google_drive' | 'onedrive' | 'sharepoint';

export interface Mention {
  type: 'user' | 'member' | 'everyone' | 'here' | 'channel';
  id: number | null;
  display: string;
}

/** What the composer can @mention / #link (GET /api/messaging/mentionables). */
export type MentionableType = 'user' | 'member' | 'channel';

export interface MentionableUser {
  id: number;
  name: string;
  email: string | null;
  role: string | null;
  avatar: string | null;
}

export interface MentionableMember {
  id: number;
  name: string;
  member_number: string | null;
}

export interface MentionableChannel {
  id: number;
  name: string;
  display_name: string | null;
  channel_type: ChannelType;
  is_private: boolean;
}

export interface Mentionables {
  users: MentionableUser[];
  members: MentionableMember[];
  channels: MentionableChannel[];
}

export interface MentionablesQuery {
  q?: string;
  channel_id?: number;
  types?: MentionableType[];
  limit?: number;
}

export type MessagePriority = 'normal' | 'high' | 'urgent';
export type ActionStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

// Message Action Template Types
export interface MessageActionTemplate {
  id: number;
  action_code: string;
  action_label: string;
  action_description: string | null;
  action_icon: string | null;
  action_type: string;
  require_confirmation: boolean;
  confirmation_message: string | null;
  action_config: Record<string, any>;
}

export interface MessageActionExecutionResult {
  success: boolean;
  execution_id: number;
  action_code: string;
  result_entity_type: string | null;
  result_entity_id: number | null;
  result_data: Record<string, any>;
}

export interface MessageActionExecution {
  id: number;
  action_code: string;
  action_label: string;
  action_icon: string | null;
  status: ActionStatus;
  result_entity_type: string | null;
  result_entity_id: number | null;
  result_data: Record<string, any> | null;
  error_message: string | null;
  executed_by_name: string | null;
  executed_at: string | null;
  completed_at: string | null;
}

// DM Channel Management Types
export type EngagementState = 'new' | 'outreach' | 'engaged' | 'active' | 'stale' | 'inactive' | 'no_response' | 'dormant' | 'opt_out';
export type AssignmentType = 'initial' | 'reassignment' | 'escalation' | 'workload_balance' | 'auto_assign';
export type DMSortBy = 'engagement_score' | 'last_activity' | 'unread_count' | 'assignment_date' | 'member_name';

export interface Message {
  id: number;
  uuid: string;
  channel_id: number;
  thread_id: number | null;
  reply_count: number;
  sender: SenderInfo;
  content: string;
  content_type: 'text' | 'rich' | 'file' | 'system' | 'announcement';
  mentions: Mention[];
  tags: string[];
  has_attachments: boolean;
  attachments: Attachment[];
  reactions: Record<string, number[]>; // emoji -> user_ids
  is_edited: boolean;
  edited_at: string | null;
  is_pinned: boolean;
  created_at: string;
  // Priority and action fields
  priority?: MessagePriority;
  is_action_required?: boolean;
  action_required_description?: string;
  assigned_to_user_id?: number;
  assigned_to_member_id?: number;
  action_due_date?: string;
  action_status?: ActionStatus;
  linked_task_id?: number;
  is_read?: boolean;
  /** Free-form message metadata (e.g. media_assets, suppress_link_previews). */
  metadata?: Record<string, any> | null;
  /** Server-derived from metadata.suppress_link_previews (list endpoint). */
  suppress_link_previews?: boolean;
  /** Read receipts (list endpoint, only on messages the caller sent). */
  read_by_count?: number | null;
  recipient_count?: number | null;
  /** DMs only: when the other side read the caller's latest message. */
  seen_at?: string | null;
  /** TRUS-702: how this message travelled (member DMs). Absent on older servers. */
  channel?: MessageChannel | null;
  /** TRUS-702: delivery of an outbound SMS / email. Absent on older servers. */
  delivery_status?: DeliveryStatus | null;
}

// ---- TRUS-702: Integrated Inbox reply channels ----

/** Where a staff reply in a member DM goes. `note` is staff-only, never delivered. */
export type ReplyChannel = 'portal' | 'sms' | 'email' | 'note';

/** Why a reply channel is unavailable (reply-options) or a send was blocked (422). */
export type ReplyBlockReason =
  | 'opted_out'
  | 'no_phone'
  | 'no_email'
  | 'no_portal_login'
  | 'sender_not_configured'
  | 'consent_missing';

export interface ReplyOption {
  channel: ReplyChannel;
  allowed: boolean;
  reason: ReplyBlockReason | string | null;
  label: string;
}

/** GET /api/messaging/channels/{id}/reply-options (staff, member DMs only). */
export interface ReplyOptions {
  options: ReplyOption[];
  default: Exclude<ReplyChannel, 'note'>;
  last_inbound_channel: 'portal' | 'sms' | 'email' | 'call' | null;
}

/** The channel a message travelled on, as the server reports it. */
export type MessageChannel =
  | 'portal'
  | 'sms'
  | 'email'
  | 'call'
  | 'voicemail'
  | 'note'
  | 'chat'
  | 'system'
  /** TRUS-703: a campaign message mirrored into the member DM (metadata.card). */
  | 'campaign';

export interface DeliveryStatus {
  state: 'queued' | 'sent' | 'delivered' | 'failed' | 'blocked';
  error: string | null;
}

/** POST /messages with `scheduled_at` answers with this instead of a Message. */
export interface ScheduledSendResult {
  scheduled: true;
  id: number;
  scheduled_at: string;
  status: 'scheduled';
  /** TRUS-697: the zone the send time was picked in. */
  timezone?: string | null;
}

export type ScheduledMessageStatus = 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled' | string;

export interface ScheduledMessage {
  id: number;
  channel_id: number;
  content: string;
  scheduled_at: string;
  status: ScheduledMessageStatus;
  metadata?: Record<string, any> | null;
  /** Backend returns these top-level (BE #1145 ScheduledMessageResponse). */
  priority?: string | null;
  thread_id?: number | null;
  attachment_ids?: number[] | null;
  sent_message_id?: number | null;
  sent_at?: string | null;
  failure_reason?: string | null;
  updated_at?: string | null;
  created_at: string;
  /** TRUS-697: the zone the send time was picked in. */
  timezone?: string | null;
}

export interface ReadReceiptReader {
  user_id: number | null;
  member_id: number | null;
  name: string;
  read_at?: string | null;
}

export interface ReadReceipts {
  message_id: number;
  recipient_count: number;
  read: ReadReceiptReader[];
  unread: ReadReceiptReader[];
}

export interface MarkActionRequiredRequest {
  is_action_required: boolean;
  assigned_to_user_id?: number;
  assigned_to_member_id?: number;
  action_due_date?: string;
}

export interface CreateTaskFromMessageRequest {
  assigned_to_user_id?: number;
  assigned_to_member_id?: number;
  due_date?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  task_type?: string;
  /** Optional overrides; the server derives both from the message when omitted. */
  title?: string;
  description?: string;
}

export interface TaskFromMessageResult {
  message_id: number;
  task_id: number;
  task_title: string;
  task_description?: string | null;
  task_status?: string;
  task_priority?: string;
  member_id?: number | null;
  member_name?: string | null;
  assigned_to_user_id?: number;
  assigned_to_member_id?: number;
  assigned_to_name?: string | null;
  deadline?: string | null;
  due_date?: string;
}

/** GET /api/messaging/messages/link-preview — unfurl metadata for one URL. */
export interface LinkPreview {
  url: string;
  final_url: string | null;
  available: boolean;
  title: string | null;
  description: string | null;
  site_name: string | null;
  image_url: string | null;
  favicon_url: string | null;
}

/** DELETE /api/messaging/channels/{id} response. */
export interface DeleteChannelResult {
  success: boolean;
  channel_id: number;
  deleted_at: string | null;
}

/** Shape returned by the upload, attach-document and attach-cloud-file endpoints. */
export interface UploadedAttachment {
  id: number;
  uuid: string;
  filename: string;
  file_type: string;
  mime_type: string;
  file_size: number;
  url: string | null;
  thumbnail_url: string | null;
  source?: AttachmentSource | null;
  provider?: CloudFileProvider | null;
  provider_file_id?: string | null;
  modified_at?: string | null;
  icon_hint?: string | null;
}

/**
 * Subset of the Documents system's DocumentFileResponse
 * (app/routers/documents.py) used by the attach picker. Field names match the
 * backend model exactly.
 */
export interface PickerDocumentFile {
  id: number;
  file_name: string;
  original_filename: string;
  mime_type: string;
  file_size_bytes: number;
  version_number: number;
  is_current: boolean;
  created_at: string | null;
}

/** Subset of the backend DocumentResponse (both /member/{id} and /search return List[DocumentResponse]). */
export interface PickerDocument {
  id: number;
  uuid: string;
  doc_type_id: number;
  doc_type_name: string | null;
  doc_subtype_name: string | null;
  category_name: string | null;
  target_type: string;
  target_id: number;
  target_name: string | null;
  program_name: string | null;
  status: string;
  uploaded_by_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  files: PickerDocumentFile[];
  file_count: number;
}

// DM Channel Management Interfaces
export interface DMChannelFilters {
  search?: string;
  assigned_to_user_id?: number;
  unassigned_only?: boolean;
  my_assignments?: boolean;
  engagement_states?: EngagementState[];
  min_engagement_score?: number;
  max_engagement_score?: number;
  days_since_last_activity?: number;
  has_unread_messages?: boolean;
  awaiting_response?: boolean;
  needs_followup?: boolean;
  linked_campaign_id?: number;
  sort_by?: DMSortBy;
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface DMChannelSummary {
  id: number;
  uuid: string;
  member_id: number | null;
  member_name: string;
  member_email: string | null;
  member_phone: string | null;
  assigned_case_manager_id: number | null;
  assigned_case_manager_name: string | null;
  assignment_date: string | null;
  engagement_state: EngagementState;
  engagement_score: number;
  last_message_at: string | null;
  last_outbound_at: string | null;
  last_inbound_at: string | null;
  days_inactive: number;
  unread_count: number;
  total_message_count: number;
  member_message_count: number;
  staff_message_count: number;
  linked_campaign_ids: number[];
  linked_communication_count: number;
  awaiting_response: boolean;
  needs_followup: boolean;
  has_opt_out: boolean;
  topic?: string | null;
  last_message_preview?: string | null;
}

export interface DMChannelListResponse {
  channels: DMChannelSummary[];
  total_count: number;
  stats: Record<string, number>;
}

export interface CaseManagerWorkload {
  user_id: number;
  user_name: string;
  email: string;
  total_assigned: number;
  active_channels: number;
  awaiting_response: number;
  needs_followup: number;
  inactive_channels: number;
  avg_engagement_score: number;
  capacity_percentage: number;
}

export interface AssignChannelRequest {
  channel_ids: number[];
  assign_to_user_id: number;
  assignment_type?: AssignmentType;
  reason?: string;
  notify_new_assignee?: boolean;
  notify_member?: boolean;
}

export interface AssignmentResult {
  success: boolean;
  assigned_count: number;
  failed_count: number;
  errors: Array<{ channel_id: number; error: string }>;
}

export interface CreateMemberDMRequest {
  member_id: number;
  assign_to_user_id?: number;
  send_welcome_message?: boolean;
  welcome_message?: string;
}

export interface DMChannelStats {
  total_channels: number;
  by_state: Record<EngagementState, number>;
  awaiting_response: number;
  needs_followup: number;
  avg_engagement_score: number;
}

export interface AssignmentHistoryEntry {
  id: number;
  from_user_id: number | null;
  from_user_name: string | null;
  to_user_id: number;
  to_user_name: string;
  assignment_type: AssignmentType;
  reason: string | null;
  assigned_at: string;
  assigned_by_user_id: number | null;
  assigned_by_name: string | null;
}

export interface UnifiedTimelineMessage {
  id: number;
  source_type: 'channel_message' | 'member_communication' | 'bulk_message';
  source_id: number;
  direction: 'inbound' | 'outbound';
  message_type: string;
  subject: string | null;
  content_preview: string;
  status: string;
  is_read: boolean;
  sender_name: string | null;
  sent_at: string | null;
  created_at: string;
  channel_id: number | null;
  campaign_id: number | null;
}

export interface Thread {
  parent_message: Message;
  replies: Message[];
  participant_count: number;
}

export interface MessageTag {
  id: number;
  name: string;
  color: string;
  description: string | null;
  usage_count: number;
}

export interface SearchResult {
  id: number;
  channel_id: number;
  channel_name: string;
  content: string;
  sender_name: string;
  created_at: string;
  highlight: string | null;
}

// Request types
export interface CreateChannelRequest {
  name: string;
  display_name?: string;
  description?: string;
  channel_type?: ChannelType;
  icon?: string;
  color?: string;
  auto_join_new_users?: boolean;
  initial_member_ids?: number[];
}

export interface UpdateChannelRequest {
  display_name?: string;
  description?: string;
  topic?: string;
  icon?: string;
  color?: string;
  is_readonly?: boolean;
  allow_threads?: boolean;
  allow_reactions?: boolean;
  allow_file_uploads?: boolean;
}

export interface SendMessageRequest {
  channel_id: number;
  content: string;
  thread_id?: number;
  priority?: MessagePriority;
  attachments?: Array<{ id: number }>;
  metadata?: Record<string, any>;
  // Action-related fields for outbound messages
  is_action_required?: boolean;
  action_required_description?: string;
  assigned_to_user_id?: number;
  assigned_to_member_id?: number;
  action_due_date?: string;
  /** ISO datetime — at least 1 minute and at most 365 days ahead. */
  scheduled_at?: string;
  /** TRUS-702: member DMs only — where the reply goes (absent = server default). */
  delivery_channel?: ReplyChannel;
  /** TRUS-702: email subject when delivery_channel is `email`. */
  subject?: string;
}

export interface ChannelFilters {
  channel_type?: ChannelType;
  include_archived?: boolean;
  include_dms?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface MessageFilters {
  before_id?: number;
  after_id?: number;
  limit?: number;
  include_threads?: boolean;
}

export interface SearchFilters {
  q: string;
  channel_ids?: number[];
  from_user_id?: number;
  has_attachments?: boolean;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

// Attachment Action Types
export interface AttachmentAction {
  id: number;
  action_code: string;
  action_label: string;
  action_description: string | null;
  action_icon: string;
  action_type: string;
  require_confirmation: boolean;
  confirmation_message: string | null;
}

export interface AttachmentActionExecution {
  id: number;
  attachment_id: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  result_entity_type: string | null;
  result_entity_id: number | null;
  result_data: Record<string, any> | null;
  error_message: string | null;
  executed_at: string;
}

export interface AttachmentLinkRequest {
  link_type: string;
  link_purpose?: string;
  form_template_id?: number;
  form_instance_id?: number;
  workflow_template_id?: number;
  workflow_step_id?: number;
  workflow_instance_id?: number;
  workflow_step_instance_id?: number;
  enrollment_id?: number;
  referral_id?: string;
  member_id?: number;
  field_mapping?: Record<string, any>;
  action_config?: Record<string, any>;
}

export interface AttachmentLink {
  id: number;
  attachment_id: number;
  link_type: string;
  link_purpose: string | null;
  status: string;
  form_template_id?: number;
  form_instance_id?: number;
  workflow_template_id?: number;
  workflow_step_id?: number;
  workflow_instance_id?: number;
  enrollment_id?: number;
  referral_id?: string;
  member_id?: number;
  field_mapping?: Record<string, any>;
  action_config?: Record<string, any>;
  created_at: string;
  processed_at?: string;
  created_by_name?: string;
}

export type DocumentCategory =
  | 'consent'
  | 'assessment'
  | 'medical_record'
  | 'id_document'
  | 'proof_of_income'
  | 'proof_of_residence'
  | 'employment_verification'
  | 'intake'
  | 'agreement'
  | 'contract'
  | 'other';

// Translation types
export interface SupportedLanguage {
  code: string;
  name: string;
}

export interface TranslationResult {
  original: string;
  translated: string;
  source_language: string;
  target_language: string;
  detected_language?: string;
  cached?: boolean;
}

export interface MessageTranslation {
  message_id: number;
  original: string;
  translated: string;
  source_language: string;
  target_language: string;
  detected_language?: string;
}

export interface LanguagePreference {
  preferred_language: string;
  auto_translate: boolean;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const messagingApi = {
  // ---- Channels ----

  /**
   * List all channels the user has access to
   */
  async listChannels(filters?: ChannelFilters): Promise<Channel[]> {
    try {
      const response = await apiClient.get('/api/messaging/channels', { params: filters });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a new channel
   */
  async createChannel(request: CreateChannelRequest): Promise<Channel> {
    try {
      const response = await apiClient.post('/api/messaging/channels', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create or get a direct message channel
   */
  async createDM(targetUserId?: number, targetMemberId?: number): Promise<Channel> {
    try {
      const response = await apiClient.post('/api/messaging/channels/dm', {
        target_user_id: targetUserId,
        target_member_id: targetMemberId,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Staff, members and channels the caller may @mention / #link (staff only;
   * member logins get 403). Members are only searched when q is non-empty.
   */
  async getMentionables(query: MentionablesQuery = {}): Promise<Mentionables> {
    try {
      const params: Record<string, string | number> = {};
      if (query.q) params.q = query.q;
      if (query.channel_id != null) params.channel_id = query.channel_id;
      if (query.types && query.types.length > 0) params.types = query.types.join(',');
      if (query.limit != null) params.limit = query.limit;
      const response = await apiClient.get('/api/messaging/mentionables', { params });
      const data = response.data || {};
      return {
        users: Array.isArray(data.users) ? data.users : [],
        members: Array.isArray(data.members) ? data.members : [],
        channels: Array.isArray(data.channels) ? data.channels : [],
      };
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get channel details
   */
  async getChannel(channelId: number): Promise<ChannelDetail> {
    try {
      const response = await apiClient.get(`/api/messaging/channels/${channelId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update channel settings
   */
  async updateChannel(channelId: number, request: UpdateChannelRequest): Promise<Channel> {
    try {
      const response = await apiClient.patch(`/api/messaging/channels/${channelId}`, request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Archive a channel
   */
  /** TRUS-638: pin a channel or DM for the caller (appended at the end). Idempotent. */
  async pinChannel(channelId: number): Promise<{ channel_id: number; is_pinned: boolean; pin_position: number | null }> {
    try {
      const response = await apiClient.put(`/api/messaging/channels/${channelId}/pin`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** TRUS-638: unpin a channel or DM for the caller. Idempotent. */
  async unpinChannel(channelId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/channels/${channelId}/pin`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** TRUS-638: reorder the caller's pins (top first). Ids that aren't pinned are ignored. */
  async reorderPins(channelIds: number[]): Promise<{ pins: Array<{ channel_id: number; pin_position: number }> }> {
    try {
      const response = await apiClient.put('/api/messaging/channels/pins/order', { channel_ids: channelIds });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async archiveChannel(channelId: number): Promise<void> {
    try {
      await apiClient.post(`/api/messaging/channels/${channelId}/archive`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Unarchive a channel
   */
  async unarchiveChannel(channelId: number): Promise<void> {
    try {
      await apiClient.post(`/api/messaging/channels/${channelId}/unarchive`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete (soft-delete) a channel for everyone. Owner/admin, org admin or
   * super admin only; member DMs return 400 and can only be archived.
   */
  async deleteChannel(channelId: number): Promise<DeleteChannelResult> {
    try {
      const response = await apiClient.delete(`/api/messaging/channels/${channelId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Unfurl a URL for a link-preview card.
   */
  async getLinkPreview(url: string): Promise<LinkPreview> {
    try {
      const response = await apiClient.get('/api/messaging/messages/link-preview', { params: { url } });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Attach an existing Documents-system record/file to a channel. Returns the
   * same shape as uploadAttachment, so the id goes into sendMessage attachments.
   */
  async attachDocument(request: {
    channel_id: number;
    doc_record_id?: number;
    doc_file_id?: number;
  }): Promise<UploadedAttachment> {
    try {
      const response = await apiClient.post('/api/messaging/messages/attach-document', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Attach a Google Drive / OneDrive / SharePoint file as a link attachment.
   * The server stores the provider's web URL (no bytes are copied) and returns
   * the same shape as uploadAttachment. 401/409 mean the account must be
   * reconnected.
   */
  async attachCloudFile(request: {
    channel_id: number;
    provider: CloudFileProvider;
    file_id: string;
    drive_id?: string | null;
  }): Promise<UploadedAttachment> {
    try {
      const body: Record<string, unknown> = {
        channel_id: request.channel_id,
        provider: request.provider,
        file_id: request.file_id,
      };
      if (request.drive_id) body.drive_id = request.drive_id;
      const response = await apiClient.post('/api/messaging/messages/attach-cloud-file', body);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Documents for the attach picker: a member's documents when memberId is
   * given, otherwise the org-wide document search (which matches file names
   * and document type names as well as notes/metadata).
   */
  async listDocumentsForPicker(options: {
    memberId?: number | null;
    query?: string;
    pageSize?: number;
  }): Promise<PickerDocument[]> {
    const page_size = options.pageSize ?? 50;
    try {
      if (options.memberId) {
        const response = await apiClient.get(`/api/documents/member/${options.memberId}`, {
          params: { page_size },
        });
        return response.data || [];
      }
      const response = await apiClient.get('/api/documents/search', {
        params: { q: options.query || undefined, page_size },
      });
      return response.data || [];
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Join a public channel
   */
  async joinChannel(channelId: number): Promise<{ member_id: number }> {
    try {
      const response = await apiClient.post(`/api/messaging/channels/${channelId}/join`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Leave a channel
   */
  async leaveChannel(channelId: number): Promise<void> {
    try {
      await apiClient.post(`/api/messaging/channels/${channelId}/leave`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Add members to a channel
   */
  async addMembers(
    channelId: number,
    userIds?: number[],
    memberIds?: number[],
    role: MemberRole = 'member'
  ): Promise<{ added: number }> {
    try {
      const response = await apiClient.post(`/api/messaging/channels/${channelId}/members`, {
        user_ids: userIds,
        member_ids: memberIds,
        role,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Remove a member from a channel
   */
  async removeMember(channelId: number, memberId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/channels/${channelId}/members/${memberId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update member role or settings
   */
  async updateMember(
    channelId: number,
    memberId: number,
    updates: { role?: MemberRole; notification_level?: NotificationLevel; muted_until?: string }
  ): Promise<void> {
    try {
      await apiClient.patch(`/api/messaging/channels/${channelId}/members/${memberId}`, updates);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Messages ----

  /**
   * Get messages for a channel
   */
  async getMessages(channelId: number, filters?: MessageFilters): Promise<Message[]> {
    try {
      const response = await apiClient.get(`/api/messaging/messages/channel/${channelId}`, {
        params: filters,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * TRUS-702: the reply channels open for a member DM, each with why it is
   * blocked, plus the default. Resolves null when the server has no such
   * endpoint (older backend) or the channel is not a member DM (404), so the
   * composer simply falls back to sending the way it always has.
   */
  async getReplyOptions(channelId: number): Promise<ReplyOptions | null> {
    try {
      const response = await apiClient.get(`/api/messaging/channels/${channelId}/reply-options`);
      const data = response.data;
      return data && Array.isArray(data.options) && data.options.length > 0 ? data : null;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 405) return null;
      throw handleApiError(error);
    }
  },

  /**
   * Send a message
   */
  async sendMessage(request: SendMessageRequest): Promise<Message> {
    try {
      const response = await apiClient.post('/api/messaging/messages', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Schedule a message: same body as sendMessage plus `scheduled_at`. The
   * server stores it and answers 201 {scheduled: true, ...} — no channel message
   * exists until it goes out.
   */
  async scheduleMessage(
    request: SendMessageRequest & { scheduled_at: string; timezone?: string },
  ): Promise<ScheduledSendResult> {
    try {
      const response = await apiClient.post('/api/messaging/messages', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** The caller's own scheduled messages, optionally for one channel. */
  async listScheduledMessages(channelId?: number): Promise<ScheduledMessage[]> {
    try {
      const response = await apiClient.get('/api/messaging/messages/scheduled', {
        params: channelId != null ? { channel_id: channelId } : undefined,
      });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async updateScheduledMessage(
    id: number,
    changes: { content?: string; scheduled_at?: string },
  ): Promise<ScheduledMessage> {
    try {
      const response = await apiClient.patch(`/api/messaging/messages/scheduled/${id}`, changes);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async cancelScheduledMessage(id: number): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/messages/scheduled/${id}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  async sendScheduledMessageNow(id: number): Promise<Message | Record<string, any>> {
    try {
      const response = await apiClient.post(`/api/messaging/messages/scheduled/${id}/send-now`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Mark everything in a channel up to (and including) a message as read. */
  /** Read up to `upToMessageId`; without it, up to the newest message (TRUS-759). */
  async markChannelRead(channelId: number, upToMessageId?: number): Promise<void> {
    try {
      await apiClient.post(`/api/messaging/messages/channel/${channelId}/read`, {
        up_to_message_id: upToMessageId ?? null,
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * TRUS-759: mark the conversation unread for the caller (its newest message
   * from someone else). `applied: false` when the caller has no channel row, so
   * there is no unread count to raise. Older backends answer 404/405.
   */
  async markChannelUnread(
    channelId: number,
  ): Promise<{ channel_id: number; message_id: number | null; unread_count: number; applied: boolean }> {
    try {
      const response = await apiClient.post(`/api/messaging/messages/channel/${channelId}/unread`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Who has / hasn't read a message (sender or channel owner/admin; 403 otherwise). */
  async getReadReceipts(messageId: number): Promise<ReadReceipts> {
    try {
      const response = await apiClient.get(`/api/messaging/messages/${messageId}/read-receipts`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get a single message
   */
  async getMessage(messageId: number): Promise<Message> {
    try {
      const response = await apiClient.get(`/api/messaging/messages/${messageId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Edit a message
   */
  async editMessage(messageId: number, content: string): Promise<Message> {
    try {
      const response = await apiClient.patch(`/api/messaging/messages/${messageId}`, { content });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Delete a message
   */
  async deleteMessage(messageId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/messages/${messageId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get a thread with replies
   */
  async getThread(messageId: number, limit?: number, offset?: number): Promise<Thread> {
    try {
      const response = await apiClient.get(`/api/messaging/messages/${messageId}/thread`, {
        params: { limit, offset },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Add a reaction to a message
   */
  async addReaction(messageId: number, emoji: string): Promise<void> {
    try {
      await apiClient.post(`/api/messaging/messages/${messageId}/reactions`, { emoji });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Remove a reaction from a message
   */
  async removeReaction(messageId: number, emoji: string): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Upload a file attachment
   */
  async uploadAttachment(channelId: number, file: File, messageId?: number): Promise<{
    id: number;
    uuid: string;
    filename: string;
    file_type: string;
    mime_type: string;
    file_size: number;
    url: string | null;
    thumbnail_url: string | null;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const params = new URLSearchParams();
      params.append('channel_id', channelId.toString());
      if (messageId) {
        params.append('message_id', messageId.toString());
      }

      const response = await apiClient.post(`/api/messaging/messages/upload?${params.toString()}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Pin a message
   */
  async pinMessage(messageId: number): Promise<void> {
    try {
      await apiClient.post(`/api/messaging/messages/${messageId}/pin`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Unpin a message
   */
  async unpinMessage(messageId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/messages/${messageId}/pin`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Search messages
   */
  async searchMessages(filters: SearchFilters): Promise<SearchResult[]> {
    try {
      const params: any = { ...filters };
      if (filters.channel_ids) {
        params.channel_ids = filters.channel_ids.join(',');
      }
      const response = await apiClient.get('/api/messaging/messages/search', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get available tags
   */
  async getTags(search?: string, limit?: number): Promise<MessageTag[]> {
    try {
      const response = await apiClient.get('/api/messaging/messages/tags', {
        params: { search, limit },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Translation ----

  /**
   * Get supported languages for translation
   */
  async getSupportedLanguages(): Promise<SupportedLanguage[]> {
    try {
      const response = await apiClient.get('/api/messaging/translate/languages');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Translate arbitrary text
   */
  async translateText(
    text: string,
    targetLanguage: string,
    sourceLanguage?: string
  ): Promise<TranslationResult> {
    try {
      const response = await apiClient.post('/api/messaging/translate/text', {
        text,
        target_language: targetLanguage,
        source_language: sourceLanguage,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Translate a specific message
   */
  async translateMessage(
    messageId: number,
    targetLanguage: string
  ): Promise<MessageTranslation> {
    try {
      const response = await apiClient.post('/api/messaging/translate/message', {
        message_id: messageId,
        target_language: targetLanguage,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get cached translation for a message
   */
  async getMessageTranslation(
    messageId: number,
    targetLanguage: string
  ): Promise<{ message_id: number; target_language: string; translated: string } | null> {
    try {
      const response = await apiClient.get(`/api/messaging/translate/message/${messageId}`, {
        params: { target_language: targetLanguage },
      });
      return response.data;
    } catch (error) {
      // Return null if not found
      return null;
    }
  },

  /**
   * Batch translate multiple messages
   */
  async batchTranslateMessages(
    messageIds: number[],
    targetLanguage: string
  ): Promise<Record<number, string>> {
    try {
      const response = await apiClient.post('/api/messaging/translate/batch', {
        message_ids: messageIds,
        target_language: targetLanguage,
      });
      return response.data.translations;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Detect language of text
   */
  async detectLanguage(text: string): Promise<{ text: string; detected_language: string; confidence: number }> {
    try {
      const response = await apiClient.post('/api/messaging/translate/detect', null, {
        params: { text },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Set user language preferences
   */
  async setLanguagePreference(
    preferredLanguage: string,
    autoTranslate: boolean,
    userId?: number,
    memberId?: number
  ): Promise<void> {
    try {
      await apiClient.put('/api/messaging/translate/preferences', {
        preferred_language: preferredLanguage,
        auto_translate: autoTranslate,
      }, {
        params: { user_id: userId, member_id: memberId },
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get user language preferences
   */
  async getLanguagePreference(userId?: number, memberId?: number): Promise<LanguagePreference> {
    try {
      const response = await apiClient.get('/api/messaging/translate/preferences', {
        params: { user_id: userId, member_id: memberId },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ============================================================================
  // ATTACHMENT ACTIONS
  // ============================================================================

  /**
   * Get available actions for an attachment
   */
  async getAttachmentActions(attachmentId: number): Promise<AttachmentAction[]> {
    try {
      const response = await apiClient.get(`/api/messaging/attachments/${attachmentId}/available-actions`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Execute an action on an attachment
   */
  async executeAttachmentAction(
    attachmentId: number,
    actionCode: string,
    config?: {
      action_config?: Record<string, any>;
      context?: Record<string, any>;
    }
  ): Promise<AttachmentActionExecution> {
    try {
      const response = await apiClient.post(
        `/api/messaging/attachments/${attachmentId}/actions/${actionCode}`,
        config || {}
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Classify/categorize a document attachment
   */
  async classifyAttachment(
    attachmentId: number,
    category: string,
    requiresAction: boolean = false,
    actionDueDate?: string
  ): Promise<{ id: number; document_category: string; requires_action: boolean; action_due_date: string | null }> {
    try {
      const response = await apiClient.patch(`/api/messaging/attachments/${attachmentId}/classify`, {
        document_category: category,
        requires_action: requiresAction,
        action_due_date: actionDueDate,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Link an attachment to an entity (form, workflow, member, etc.)
   */
  async linkAttachment(
    attachmentId: number,
    linkRequest: AttachmentLinkRequest
  ): Promise<AttachmentLink> {
    try {
      const response = await apiClient.post(`/api/messaging/attachments/${attachmentId}/link`, linkRequest);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get all links for an attachment
   */
  async getAttachmentLinks(attachmentId: number): Promise<AttachmentLink[]> {
    try {
      const response = await apiClient.get(`/api/messaging/attachments/${attachmentId}/links`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Remove a link from an attachment
   */
  async deleteAttachmentLink(attachmentId: number, linkId: number): Promise<void> {
    try {
      await apiClient.delete(`/api/messaging/attachments/${attachmentId}/links/${linkId}`);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get channel messages with translations
   */
  async getTranslatedChannelMessages(
    channelId: number,
    targetLanguage: string,
    limit?: number,
    offset?: number
  ): Promise<{
    channel_id: number;
    target_language: string;
    messages: Array<{
      id: number;
      content: string;
      detected_language: string | null;
      created_at: string | null;
      sender_user_id: number | null;
      sender_member_id: number | null;
      translated_content: string | null;
      has_translation: boolean;
    }>;
  }> {
    try {
      const response = await apiClient.get(`/api/messaging/translate/channel/${channelId}/messages`, {
        params: { target_language: targetLanguage, limit, offset },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Message Priority and Actions ----

  /**
   * Set message priority
   */
  async setMessagePriority(messageId: number, priority: MessagePriority): Promise<{ message_id: number; priority: MessagePriority }> {
    try {
      const response = await apiClient.patch(`/api/messaging/messages/${messageId}/priority`, { priority });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Mark message as requiring action
   */
  async markActionRequired(messageId: number, request: MarkActionRequiredRequest): Promise<any> {
    try {
      const response = await apiClient.patch(`/api/messaging/messages/${messageId}/action-required`, request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Mark a specific message as read
   */
  async markMessageRead(messageId: number): Promise<{ message_id: number; is_read: boolean }> {
    try {
      const response = await apiClient.post(`/api/messaging/messages/${messageId}/mark-read`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Mark a specific message as unread
   */
  async markMessageUnread(messageId: number): Promise<{ message_id: number; is_read: boolean }> {
    try {
      const response = await apiClient.post(`/api/messaging/messages/${messageId}/mark-unread`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a task from a message
   */
  async createTaskFromMessage(messageId: number, request: CreateTaskFromMessageRequest): Promise<TaskFromMessageResult> {
    try {
      const response = await apiClient.post(`/api/messaging/messages/${messageId}/create-task`, request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update action status on a message
   */
  async updateActionStatus(messageId: number, status: ActionStatus): Promise<{ message_id: number; action_status: ActionStatus }> {
    try {
      const response = await apiClient.patch(`/api/messaging/messages/${messageId}/action-status`, null, {
        params: { status },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- Message Action Templates ----

  /**
   * Get available action templates for a message
   */
  async getMessageAvailableActions(messageId: number): Promise<MessageActionTemplate[]> {
    try {
      const response = await apiClient.get(`/api/messaging/messages/${messageId}/available-actions`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Execute an action template on a message
   */
  async executeMessageAction(
    messageId: number,
    actionCode: string,
    configOverride?: Record<string, any>
  ): Promise<MessageActionExecutionResult> {
    try {
      const response = await apiClient.post(
        `/api/messaging/messages/${messageId}/actions/${actionCode}`,
        { action_config_override: configOverride }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get action execution history for a message
   */
  async getMessageActionHistory(messageId: number): Promise<MessageActionExecution[]> {
    try {
      const response = await apiClient.get(`/api/messaging/messages/${messageId}/action-history`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // ---- DM Channel Management ----

  /**
   * List DM channels with advanced filtering
   */
  async listDMChannels(filters: DMChannelFilters = {}): Promise<DMChannelListResponse> {
    try {
      const params: Record<string, any> = { ...filters };
      // Convert array to comma-separated string for engagement_states
      if (filters.engagement_states?.length) {
        params.engagement_states = filters.engagement_states.join(',');
      }
      const response = await apiClient.get('/api/messaging/dm-management/channels', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get case manager workload summary
   */
  async getCaseManagerWorkload(): Promise<CaseManagerWorkload[]> {
    try {
      const response = await apiClient.get('/api/messaging/dm-management/workload');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get available staff for channel assignment
   */
  async getAvailableStaff(): Promise<CaseManagerWorkload[]> {
    try {
      const response = await apiClient.get('/api/messaging/dm-management/available-staff');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get DM channel statistics
   */
  async getDMChannelStats(): Promise<DMChannelStats> {
    try {
      const response = await apiClient.get('/api/messaging/dm-management/stats');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Assign channels to a case manager
   */
  async assignChannels(request: AssignChannelRequest): Promise<AssignmentResult> {
    try {
      const response = await apiClient.post('/api/messaging/dm-management/assign', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Create a DM channel for a member
   */
  async createMemberDM(request: CreateMemberDMRequest): Promise<{
    channel_id: number;
    uuid: string;
    display_name: string;
    engagement_state: EngagementState;
    member: { first_name: string; last_name: string; email: string };
  }> {
    try {
      const response = await apiClient.post('/api/messaging/dm-management/create-member-dm', request);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get assignment history for a channel
   */
  async getAssignmentHistory(channelId: number): Promise<{ history: AssignmentHistoryEntry[] }> {
    try {
      const response = await apiClient.get(`/api/messaging/dm-management/assignment-history/${channelId}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Update engagement state for a channel
   */
  async updateChannelEngagement(channelId: number): Promise<{ channel_id: number; new_engagement_state: EngagementState }> {
    try {
      const response = await apiClient.post(`/api/messaging/dm-management/channels/${channelId}/update-engagement`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Mark a channel as opted out
   */
  async markChannelOptOut(channelId: number, reason?: string): Promise<{ success: boolean; channel_id: number; status: string }> {
    try {
      const response = await apiClient.post(`/api/messaging/dm-management/channels/${channelId}/opt-out`, null, {
        params: reason ? { reason } : undefined,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Reactivate an opted-out or dormant channel
   */
  async reactivateChannel(channelId: number): Promise<{ success: boolean; channel_id: number; new_engagement_state: EngagementState }> {
    try {
      const response = await apiClient.post(`/api/messaging/dm-management/channels/${channelId}/reactivate`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Manually set the engagement state for a DM channel
   */
  async setEngagementState(
    channelId: number,
    engagementState: EngagementState,
    reason?: string
  ): Promise<{ success: boolean; channel_id: number; old_engagement_state: EngagementState; new_engagement_state: EngagementState }> {
    try {
      const response = await apiClient.patch(`/api/messaging/dm-management/channels/${channelId}/engagement-state`, {
        engagement_state: engagementState,
        reason,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Get unified communication timeline for a member
   */
  async getMemberTimeline(
    memberId: number,
    options: { message_types?: string; limit?: number; offset?: number } = {}
  ): Promise<{ timeline: UnifiedTimelineMessage[]; total: number }> {
    try {
      const response = await apiClient.get(`/api/messaging/dm-management/members/${memberId}/timeline`, {
        params: options,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// ============================================================================
// WEBSOCKET TYPES AND HELPERS
// ============================================================================

export interface SocketMessage {
  type: 'new_message' | 'message_edited' | 'message_deleted' | 'user_typing' | 'user_stopped_typing' |
        'reaction_added' | 'reaction_removed' | 'presence_update' | 'mention_notification';
  data: any;
}

export interface TypingUser {
  user_id: number | null;
  member_id: number | null;
  name?: string;
}

/**
 * Create a WebSocket connection for real-time messaging
 */
export function createMessagingSocket(
  userId: number | null,
  memberId: number | null,
  organizationId: number,
  token: string
): WebSocket | null {
  const wsUrl = import.meta.env.VITE_WEBSOCKET_URL ?? 'wss://weavein-api.onrender.com';

  try {
    const socket = new WebSocket(`${wsUrl}/socket.io/?EIO=4&transport=websocket`);

    socket.onopen = () => {
      // Send authentication
      socket.send(JSON.stringify({
        type: 'auth',
        user_id: userId,
        member_id: memberId,
        organization_id: organizationId,
        token: token,
      }));
    };

    return socket;
  } catch (error) {
    console.error('Failed to create WebSocket connection:', error);
    return null;
  }
}
